// hooks/useNotification.ts
import { CallActionTypes, CallBaseMessage } from '@numeo/types'
import { WebSocketStatus } from '@numeo/types-core'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store'
import { addNotification, clearAll, deleteNotification } from '../store/notificationSlice'
export enum Category {
    LoadFinderTruck = 'loadFinderTruck',
    Email = 'email',
    CallCenter = 'callCenter',
    Error = 'error',
    Broker = 'broker',
    Settings = 'settings',
    Updater = 'updater',
    System = 'system',
    BoxTruck = 'boxTruck',
}
export type INotification = {
    id: string
    category: Category
    message: string
    secondaryMessage?: string
    timestamp: number
    type: 'info' | 'warning' | 'error'
    path?: string
    topic?: string
    onClick?: () => void
}

export interface NotificationHook {
    notifications: INotification[]
    wsStatus: WebSocketStatus
    addNotification: (notification: INotification) => void
    deleteNotification: (notification: INotification) => void
    clearNotification: (notification: INotification) => void
    clearAll: () => void
}

export const useNotification = (): NotificationHook => {
    const dispatch = useDispatch()
    const notifications = useSelector((state: RootState) => state.notification.notifications)
    // Get wsStatus from webSocketSlice (single source of truth)
    const wsStatus = useSelector((state: RootState) => state.webSocket.status)

    const handleAddNotification = useCallback((notification: INotification) => dispatch(addNotification(notification)), [dispatch])
    const handleDeleteNotification = useCallback((notification: INotification) => dispatch(deleteNotification(notification.id)), [dispatch])
    const handleClearAll = useCallback(() => dispatch(clearAll()), [dispatch])

    return useMemo(
        () => ({
            notifications,
            wsStatus,
            addNotification: handleAddNotification,
            deleteNotification: handleDeleteNotification,
            clearNotification: handleDeleteNotification,
            clearAll: handleClearAll,
        }),
        [notifications, wsStatus, handleAddNotification, handleDeleteNotification, handleClearAll]
    )
}

// =========== Helper classes ===========
export class CallCenterNotification implements INotification {
    readonly id: string
    readonly category: Category
    readonly message: string
    readonly secondaryMessage: string
    readonly timestamp: number
    readonly type: 'info' | 'warning' | 'error'
    readonly path?: string | undefined
    readonly topic: string

    constructor(message: CallBaseMessage) {
        let path = 'dispatcher/call-center'
        let topic = 'Call Center'
        switch (message.data.action) {
            case CallActionTypes.CC_CallStarted:
                this.message = 'Call started'
                this.secondaryMessage = `Call ID: ${message.data.call.callId}`
                break
            case CallActionTypes.CC_CallUpdated:
                this.message = 'Call updated'
                this.secondaryMessage = `Call ID: ${message.data.call.callId}`
                break
            case CallActionTypes.CC_CallEnded:
                this.message = 'Call ended'
                this.secondaryMessage = `Call ID: ${message.data.callId}`
                path = 'dispatcher/call-center?call_type=past'
                topic = 'temp'
                break
            default:
                this.message = 'Unknown action'
                this.secondaryMessage = `...`
                break
        }
        this.id = ''
        this.category = Category.CallCenter
        this.timestamp = Date.now()
        this.type = 'info'
        this.path = path
        this.topic = topic || 'Call Center'
    }
    toJson() {
        return {
            id: this.id,
            category: this.category,
            message: this.message,
            secondaryMessage: this.secondaryMessage,
            timestamp: this.timestamp,
            type: this.type,
            path: this.path,
        }
    }
}
